import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate Supabase configuration
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase configuration. Please check your environment variables.');
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'X-Client-Info': 'unimentor-app'
    }
  }
});

// Test Supabase connection
const testConnection = async () => {
  try {
    const { data, error } = await supabase
      .from('universities')
      .select('count')
      .limit(1);
    
    if (error && error.code !== 'PGRST116') {
      console.error('Supabase connection test failed:', error);
    }
  } catch (error) {
    console.error('Supabase connection error:', error);
  }
};

// Test connection on initialization
testConnection();

// Enhanced error handling for Supabase operations
const handleSupabaseError = (error, operation, context = {}) => {
  // Log error without circular dependency
  console.error('Supabase error:', operation, error, context);

  // Transform Supabase errors to user-friendly messages
  if (error.code === 'PGRST116') {
    throw new Error('Record not found');
  } else if (error.code === '23505') {
    throw new Error('This record already exists');
  } else if (error.code === '23503') {
    throw new Error('Cannot delete: record is referenced by other data');
  } else if (error.message.includes('JWT')) {
    throw new Error('Session expired. Please log in again.');
  }

  throw error;
};
// Helper function to get current user profile with university info
export const getCurrentUserProfile = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile, error } = await supabase
    .from('user_profiles')
    .select(`
      *,
      university:universities(*)
    `)
    .eq('user_id', user.id)
    .single();

  if (error) {
    handleSupabaseError(error, 'getCurrentUserProfile', { userId: user.id });
  }
  return { user, profile };
};

// Helper function to check user permissions
export const checkPermission = async (action, resource = null) => {
  try {
    const { profile } = await getCurrentUserProfile();
    
    // Super admin has all permissions
    if (profile.role === 'super_admin') return true;
    
    // University admin has permissions within their university
    if (profile.role === 'university_admin') {
      return resource ? resource.university_id === profile.university_id : true;
    }
    
    // Agents have limited permissions
    if (profile.role === 'agent' || profile.role === 'sub_agent') {
      if (action === 'read') return true;
      if (action === 'write' && resource) {
        return resource.agent_id === profile.id || resource.university_id === profile.university_id;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Permission check failed:', error);
    return false;
  }
};

// Audit logging function
export const logAuditEvent = async (action, resourceType, resourceId, oldValues = null, newValues = null) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from('user_profiles')
      .select('university_id')
      .eq('user_id', user.id)
      .single();

    await supabase.from('audit_logs').insert({
      user_id: user.id,
      university_id: profile?.university_id,
      action,
      resource_type: resourceType,
      resource_id: resourceId,
      old_values: oldValues,
      new_values: newValues
    });
  } catch (error) {
    console.error('Audit logging failed:', error);
  }
};